import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ChevronDown,
  Database,
  ExternalLink,
  FilePlus2,
  FileText,
  Globe,
  Info,
  RefreshCw,
  Square
} from "lucide-react";
import {
  Fragment,
  useEffect,
  useRef,
  useState,
  type FC,
  type ReactNode
} from "react";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { StatusBadge } from "@/components/ui/status-badge";
import { queryClient } from "@/lib/query/queryClient";
import { cn } from "@/lib/utils";

import type { Artifact } from "../../caseIde/types";
import { KeyFactsAPI } from "../api";
import type { KeyFactActions } from "../hooks/useKeyFactActions";
import { headlineFlag, sourceLabel, valueRows } from "../model";
import type {
  AnalysisState,
  KeyFact,
  KeyFactDependent,
  KeyFactSource
} from "../types";

const ANALYSIS: Record<
  AnalysisState,
  { label: string; tone: "success" | "warn" | "danger" | "neutral" }
> = {
  current: { label: "Current", tone: "success" },
  outdated: { label: "Out of Date", tone: "warn" },
  running: { label: "Writing", tone: "neutral" },
  queued: { label: "Queued", tone: "neutral" },
  failed: { label: "Failed", tone: "danger" },
  cancelled: { label: "Stopped", tone: "neutral" }
};

const Row: FC<{ label: string; children: ReactNode }> = ({
  label,
  children
}) => (
  <>
    <dt className="pt-0.5 text-sm text-fg-3">{label}</dt>
    <dd className="min-w-0">{children}</dd>
  </>
);

const SOURCE_ICON = {
  dataset: Database,
  document: FileText,
  web: Globe
} as const;

const SourceChip: FC<{
  fact: KeyFact;
  source: KeyFactSource;
  actions: KeyFactActions;
  onOpen: (artifact: Artifact) => void;
}> = ({ fact, source, actions, onOpen }) => {
  const Icon = SOURCE_ICON[source.kind as keyof typeof SOURCE_ICON] ?? Info;
  const excluded = fact.excluded_sources.includes(source.id);
  const canChange = !fact.locked && source.kind !== "dataset";
  const name = sourceLabel(source);
  const chip = cn(
    "inline-flex h-6 max-w-full min-w-0 items-center gap-1 rounded-md bg-fill-2 px-1.5 text-sm text-fg-2",
    excluded && "line-through opacity-60"
  );
  const label = (
    <>
      <Icon className="size-3 shrink-0 text-fg-3" aria-hidden />
      <span className="truncate">{name}</span>
    </>
  );
  return (
    <li className="flex max-w-full min-w-0 items-center gap-1">
      {source.kind === "document" && source.node_id ? (
        <button
          type="button"
          title={source.quote || source.summary || undefined}
          className={cn(chip, "hover:bg-fill-3")}
          onClick={() =>
            onOpen({
              kind: "file",
              id: source.node_id as string,
              title: source.title || name,
              page: source.page ?? null
            })
          }
        >
          {label}
        </button>
      ) : source.url ? (
        <a
          href={source.url}
          target="_blank"
          rel="noreferrer"
          title={source.summary || undefined}
          className={cn(chip, "hover:bg-fill-3")}
        >
          {label}
          <ExternalLink className="size-3 shrink-0" aria-hidden />
        </a>
      ) : (
        <span className={chip} title={source.summary || undefined}>
          {label}
        </span>
      )}
      {source.kind === "web" &&
      source.artifact_path &&
      !source.exhibit_node_id ? (
        <Button
          size="xs"
          variant="ghost"
          disabled={actions.busy === `${fact.key}:exhibit:${source.id}`}
          onClick={() => void actions.fileAsExhibit(fact.key, source.id)}
        >
          <FilePlus2 className="size-3" /> Add as Exhibit
        </Button>
      ) : null}
      {canChange ? (
        <Button
          size="xs"
          variant="ghost"
          className="text-fg-3"
          aria-label={`${excluded ? "Use" : "Don't Use"} ${name}`}
          onClick={() =>
            void actions.act(fact.key, {
              action: excluded ? "include_source" : "exclude_source",
              source: source.id
            })
          }
        >
          {excluded ? "Use Again" : "Don't Use"}
        </Button>
      ) : null}
    </li>
  );
};

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** "4 letters and 2 forms": what uses the value, in a line. */
export const usedBySummary = (
  used: readonly Exclude<KeyFactDependent, { kind: "analysis" }>[]
): string => {
  const drafts = used.filter(
    (d) => d.kind === "draft" || d.kind === "exhibit_list"
  ).length;
  const forms = used.filter((d) => d.kind === "form").length;
  const facts = used.filter((d) => d.kind === "key_fact").length;
  return [
    drafts ? plural(drafts, "draft") : "",
    forms ? plural(forms, "form") : "",
    facts ? plural(facts, "insight") : ""
  ]
    .filter(Boolean)
    .join(", ")
    .replace(/, ([^,]*)$/, " and $1");
};

const UsedBy: FC<{
  individualId: string;
  fact: KeyFact;
  onOpen: (artifact: Artifact) => void;
}> = ({ individualId, fact, onOpen }) => {
  const [open, setOpen] = useState(false);
  const query = useQuery(
    {
      queryKey: ["key-facts", individualId, "used-by", fact.key],
      queryFn: () => KeyFactsAPI.get(individualId, fact.key),
      staleTime: 15_000,
      placeholderData: keepPreviousData
    },
    queryClient
  );
  const { refetch } = query;
  const seen = useRef(fact.updated_at);
  useEffect(() => {
    if (seen.current === fact.updated_at) return;
    seen.current = fact.updated_at;
    void refetch();
  }, [fact.updated_at, refetch]);
  const used = (query.data?.used_by ?? []).filter(
    (d): d is Exclude<KeyFactDependent, { kind: "analysis" }> =>
      d.kind !== "analysis"
  );
  if (query.isPending) return <Spinner className="mt-0.5 size-3.5" />;
  if (query.isError && !query.data)
    return (
      <p className="flex items-center gap-2 text-sm text-danger">
        Couldn't load what uses it.
        <Button size="xs" variant="ghost" onClick={() => void refetch()}>
          Retry
        </Button>
      </p>
    );
  if (!used.length)
    return <p className="pt-0.5 text-sm text-fg-3">Nothing uses it yet.</p>;
  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex w-fit items-center gap-1 pt-0.5 text-sm text-fg-2 hover:text-fg"
      >
        Used by {usedBySummary(used)}
        <ChevronDown
          className={cn("size-3 transition-transform", open && "rotate-180")}
          aria-hidden
        />
      </button>
      {open ? (
        <ul className="flex flex-col gap-0.5 text-sm">
          {used.map((d, i) => (
            <li key={i}>
              {d.kind === "form" ? (
                <button
                  type="button"
                  className="hover:underline"
                  onClick={() =>
                    onOpen({ kind: "form", id: d.form_id, title: d.title })
                  }
                >
                  {d.title}
                  <span className="text-fg-3">
                    {d.fields?.length
                      ? ` · ${plural(d.fields.length, "field")}`
                      : " · states it"}
                  </span>
                </button>
              ) : d.kind === "key_fact" ? (
                <span>
                  {d.label}
                  <span className="text-fg-3"> · built on it</span>
                </span>
              ) : (
                <button
                  type="button"
                  className="hover:underline"
                  onClick={() =>
                    onOpen({ kind: "draft", id: d.draft_id, title: d.title })
                  }
                >
                  {d.title}
                  <span className="text-fg-3">
                    {d.fact_fields
                      ? ` · ${plural(d.fact_fields, "fact field")}`
                      : " · states it"}
                  </span>
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

const Analysis: FC<{
  fact: KeyFact;
  actions: KeyFactActions;
  onOpen: (artifact: Artifact) => void;
}> = ({ fact, actions, onOpen }) => {
  const { analysis } = fact;
  const state = fact.analysis_state ?? null;
  const live = state === "running" || state === "queued";
  const busy = (a: string) => actions.busy === `${fact.key}:${a}`;
  return (
    <div className="flex flex-col gap-1.5 text-sm">
      <div className="flex flex-wrap items-center gap-1.5">
        {state ? (
          <StatusBadge tone={ANALYSIS[state].tone}>
            {live ? <Spinner className="size-3" /> : null}
            {ANALYSIS[state].label}
          </StatusBadge>
        ) : (
          <span className="text-fg-3">No memo yet.</span>
        )}
        {analysis?.draft_id ? (
          <Button
            size="xs"
            variant="link"
            onClick={() =>
              onOpen({
                kind: "draft",
                id: analysis.draft_id as string,
                title: `${fact.label} Analysis`
              })
            }
          >
            Open Memo
          </Button>
        ) : null}
        {live ? (
          <Button
            size="xs"
            variant="ghost"
            disabled={busy("cancel")}
            onClick={() => void actions.cancelAnalysis(fact.key)}
          >
            <Square className="size-3" /> Stop
          </Button>
        ) : (
          <Button
            size="xs"
            variant="ghost"
            disabled={busy("rerun_analysis") || fact.status === "disabled"}
            onClick={() =>
              void actions.act(fact.key, { action: "rerun_analysis" })
            }
          >
            <RefreshCw className="size-3" />
            {analysis ? "Run Again" : "Write Memo"}
          </Button>
        )}
      </div>
      {analysis?.short_answer ? (
        <p className="leading-relaxed text-fg-2">{analysis.short_answer}</p>
      ) : null}
      {analysis?.error && state === "failed" ? (
        <p className="text-danger">{analysis.error}</p>
      ) : null}
      {analysis?.gaps?.length ? (
        <ul className="list-disc pl-4 text-fg-3">
          {analysis.gaps.map((gap, i) => (
            <li key={i}>{gap}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

/**
 * Behind a row's Sources and Details: why, the value's parts, the other
 * flags, the sources as chips, what uses the value and its memo.
 */
export const FactDetails: FC<{
  individualId: string;
  fact: KeyFact;
  actions: KeyFactActions;
  onOpen: (artifact: Artifact) => void;
}> = ({ individualId, fact, actions, onOpen }) => {
  const parts = valueRows(fact.value, fact.paths);
  const headline = headlineFlag(fact);
  const flags = fact.flags.filter((f) => f.message !== headline);
  return (
    <dl
      className="mt-2 grid grid-cols-[minmax(0,5rem)_minmax(0,1fr)] gap-x-3 gap-y-2.5 rounded-md bg-fill-1 px-3 py-2.5"
      data-testid={`fact-details-${fact.key}`}
    >
      {fact.rationale ? (
        <Row label="Why">
          <p className="text-base leading-relaxed whitespace-pre-line text-fg-2">
            {fact.rationale}
          </p>
        </Row>
      ) : null}
      {parts.length > 1 ? (
        <Row label="Parts">
          <dl className="grid grid-cols-[minmax(6rem,auto)_1fr] gap-x-3 gap-y-0.5 pt-0.5 text-sm">
            {parts.map(([label, text]) => (
              <Fragment key={label}>
                <dt className="text-fg-3">{label}</dt>
                <dd className="line-clamp-3 min-w-0 break-words text-fg">
                  {text}
                </dd>
              </Fragment>
            ))}
          </dl>
        </Row>
      ) : null}
      {flags.length ? (
        <Row label="Flags">
          <ul className="flex flex-col gap-1 pt-0.5">
            {flags.map((flag, i) => (
              <li key={i} className="flex items-start gap-1.5 text-sm">
                <AlertTriangle
                  className={cn(
                    "mt-0.5 size-3 shrink-0",
                    flag.severity === "must" ? "text-warn" : "text-fg-3"
                  )}
                  aria-hidden
                />
                <span className="text-fg-2">{flag.message}</span>
              </li>
            ))}
          </ul>
        </Row>
      ) : null}
      {fact.sources.length ? (
        <Row label="Sources">
          <ul className="flex flex-wrap gap-1.5">
            {fact.sources.map((source) => (
              <SourceChip
                key={source.id}
                fact={fact}
                source={source}
                actions={actions}
                onOpen={onOpen}
              />
            ))}
          </ul>
        </Row>
      ) : null}
      <Row label="Used By">
        <UsedBy individualId={individualId} fact={fact} onOpen={onOpen} />
      </Row>
      {fact.has_analysis || fact.analysis ? (
        <Row label="Analysis">
          <Analysis fact={fact} actions={actions} onOpen={onOpen} />
        </Row>
      ) : null}
    </dl>
  );
};
